<template>
  <week-plan-view
    :plan="plan"
    :recipes="recipes"
    :week-start-day="weekStartDay"
    :unit-system="settingStore.unitSystem"
    :grocery-client="groceryClient"
    :week-planner="weekPlanner"
    :is-current-week="plan.weekStart === currentWeek"
    :suggestions="suggestions"
    :copying="copying"
    :copy-result="copyResult"
    :add-result="addResult"
    @set-meal="setMeal"
    @remove-meal="removeMeal"
    @previous-week="showWeek(shiftWeek(plan.weekStart, -1))"
    @next-week="showWeek(shiftWeek(plan.weekStart, 1))"
    @this-week="showWeek(currentWeek)"
    @copy-last-week="copyLastWeek"
    @add-meals="addMeals"
  />
</template>

<script lang="ts">
  import { computed, defineComponent, ref } from 'vue';
  import { useFirestore } from 'vuefire';
  import { useAuthStore } from '@/stores/auth';
  import { useSettingStore } from '@/stores/settings';
  import WeekPlanClient, {
    getWeekStart,
  } from '@/service/weekplan/weekplanClient';
  import { shiftWeek, suggestRecipes } from '@/service/weekplan/history';
  import GroceryClient from '@/service/groceries/groceryClient';
  import WeekPlanner from '@/service/weekplan/weekPlanner';
  import { firebase } from '@/config/Firebase';
  import RecipeClient from '@/service/recipe/recipeClient';
  import { useRecipeStore } from '@/stores/recipe';
  import WeekPlanView from '@/views/weekplan/WeekPlanView.vue';
  import type {
    Day,
    MealEntry,
    Slot,
    WeekPlan,
  } from '@/service/weekplan/types';

  // Suggestions leave out recipes planned in this many weeks before
  const SUGGEST_AFTER_WEEKS = 4;

  export default defineComponent({
    name: 'WeekPlanViewRoute',
    components: { WeekPlanView },
    async setup() {
      const firestore = useFirestore();
      const authStore = useAuthStore();
      const settingStore = useSettingStore();
      const uid = authStore.getIdentifier().uid;
      const weekStartDay = settingStore.weekStartDay;
      const currentWeek = getWeekStart(weekStartDay);

      const weekplanClient = new WeekPlanClient(firestore, uid);
      const plan = ref<WeekPlan>(await weekplanClient.planFor(currentWeek));
      const recentPlans = ref<WeekPlan[]>(
        await weekplanClient.recentPlans(
          shiftWeek(currentWeek, -SUGGEST_AFTER_WEEKS),
        ),
      );
      let requestedWeek = currentWeek;

      const recipeClient = await RecipeClient.setup(authStore.getIdentifier());
      const groceryClient = await GroceryClient.setup(
        firestore,
        authStore.getIdentifier(),
      );
      const recipeStore = useRecipeStore(recipeClient);
      const recipes = recipeStore.getRecipes();

      // The shown plan as it is now, with the stored ones of the other weeks
      const suggestions = computed(() =>
        suggestRecipes(
          recipes.value,
          [
            ...recentPlans.value.filter(
              ({ weekStart }) => weekStart !== plan.value.weekStart,
            ),
            plan.value,
          ],
          plan.value.weekStart,
          { weeks: SUGGEST_AFTER_WEEKS },
        ),
      );

      const copying = ref(false);
      const copyResult = ref<{ copied: number }>();
      const addResult = ref<{ added: number }>();

      return {
        plan,
        recipes,
        weekStartDay,
        settingStore,
        groceryClient,
        currentWeek,
        suggestions,
        copying,
        copyResult,
        addResult,
        weekPlanner: new WeekPlanner(firebase.functions),
        shiftWeek,
        async showWeek(weekStart: string) {
          // Clicking through weeks quickly: only the last one is shown
          requestedWeek = weekStart;
          const [shown, recent] = await Promise.all([
            weekplanClient.planFor(weekStart),
            weekplanClient.recentPlans(
              shiftWeek(weekStart, -SUGGEST_AFTER_WEEKS),
            ),
          ]);
          if (requestedWeek !== weekStart) return;
          plan.value = shown;
          recentPlans.value = recent;
        },
        async copyLastWeek() {
          copying.value = true;
          const result = await weekplanClient.copyFromPreviousWeek(plan.value);
          plan.value = result.plan;
          copyResult.value = { copied: result.copied };
          copying.value = false;
        },
        async addMeals(meals: WeekPlan['meals']) {
          const result = await weekplanClient.addMeals(plan.value, meals);
          plan.value = result.plan;
          addResult.value = { added: result.added };
        },
        async setMeal(day: Day, slot: Slot, entry: MealEntry) {
          plan.value = await weekplanClient.setMeal(
            plan.value,
            day,
            slot,
            entry,
          );
        },
        async removeMeal(day: Day, slot: Slot) {
          plan.value = await weekplanClient.removeMeal(plan.value, day, slot);
        },
      };
    },
  });
</script>
